import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn';
import { Button } from '../primitives/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/Select';
import { DateRangeField } from './DateRangeField';

/**
 * PeriodFilter — the period selector for dashboards and reports.
 *
 * Presentational and fully controlled. It owns NO date arithmetic: resolving a
 * preset to an actual range needs SAST boundaries (`public.sast_date` /
 * `sastDateKey`), and those live in the portal, not in the design system. A
 * shared component that knew about SAST would be a domain component wearing a
 * design-system badge. The consumer resolves the range; this renders the choice.
 *
 * Semantics: a `radiogroup`, not a row of toggle buttons. The presets are
 * mutually exclusive, so radio is the honest role — and it gives ONE tab stop
 * with arrow-key movement instead of six separate stops.
 *
 * Selected chip is `variant="primary"` = `bg-primary text-primary-foreground`, which
 * is 5.09:1 and passes AA. White on the brand orange is 2.26:1 and is never used.
 */

export type PeriodPreset =
  | 'this-month'
  | 'last-30'
  | 'this-quarter'
  | 'ytd'
  | 'all-time'
  | 'custom'
  // Closed presets (v2, 2026-09-13). Every preset above resolves to an OPEN
  // range (`end: null`) on purpose — a dashboard left open overnight must not
  // stop counting at midnight. That is wrong for a table: "show me July's
  // invoices" is the commonest table question and previously required Custom
  // plus two typed dates. These three resolve to a CLOSED range.
  | 'last-month'
  | 'last-quarter'
  | 'last-year';

export const PERIOD_LABELS: Record<PeriodPreset, string> = {
  'this-month': 'This month',
  'last-30': 'Last 30 days',
  'this-quarter': 'This quarter',
  ytd: 'Year to date',
  'all-time': 'All time',
  custom: 'Custom…',
  'last-month': 'Last month',
  'last-quarter': 'Last quarter',
  'last-year': 'Last year',
};

/**
 * Presets that resolve to a CLOSED range, and are therefore steppable.
 *
 * Stepping an open preset is meaningless — there is no "the month before this
 * month so far" — so the arrows are only offered for these.
 */
export const CLOSED_PRESETS: ReadonlySet<PeriodPreset> = new Set<PeriodPreset>([
  'last-month',
  'last-quarter',
  'last-year',
]);

/**
 * UNCHANGED, deliberately. `SalesDashboard` and `SalesCommissionPage` pass no
 * `presets` prop and must render exactly as they did before v2 — so the closed
 * presets are opt-in and do not join this list. Tables pass an explicit array.
 */
const DEFAULT_PRESETS: PeriodPreset[] = [
  'this-month',
  'last-30',
  'this-quarter',
  'ytd',
  'all-time',
  'custom',
];

/** The preset list a TABLE should offer (Rule T-7 applies — see `PeriodFilterProps.presets`). */
export const TABLE_PRESETS: PeriodPreset[] = [
  'all-time',
  'this-month',
  'last-month',
  'this-quarter',
  'last-quarter',
  'ytd',
  'last-year',
  'custom',
];

export interface PeriodFilterProps {
  value: PeriodPreset;
  /** ISO `YYYY-MM-DD`, used only when `value === 'custom'`. */
  from?: string;
  to?: string;
  onChange: (next: { preset: PeriodPreset; from?: string; to?: string }) => void;
  /**
   * Restrict or reorder the offered presets.
   *
   * RULE T-7 (ruling 2026-09-13): never offer a preset the underlying query
   * cannot reach. A page whose service fetches the 200 most recent rows and
   * whose table grows by 300 a year must not offer `last-year` — the control
   * would return an empty table and the honest reading of that screen is "there
   * were none". Trim this list to what the query can answer.
   */
  presets?: PeriodPreset[];
  /**
   * Presentation. `chips` is the v1 radiogroup (dashboards). `menu` is a single
   * `Select` for tables, where eight chips is more chrome than the table.
   */
  variant?: 'chips' | 'menu';
  /**
   * Show prev/next arrows. Rendered only when the current preset is CLOSED —
   * stepping "this month" or "year to date" has no meaning.
   *
   * Stepping emits a resolved `custom` range rather than inventing an offset
   * vocabulary, so the URL always says exactly which dates are on screen.
   */
  steppable?: boolean;
  /** Resolves a step for the consumer. Required when `steppable`; the design system owns no date maths (AR-3). */
  onStep?: (direction: -1 | 1) => void;
  /** Screen-reader name for the group. */
  label?: string;
  className?: string;
}

function PeriodFilter({
  value,
  from,
  to,
  onChange,
  presets = DEFAULT_PRESETS,
  variant = 'chips',
  steppable = false,
  onStep,
  label = 'Period',
  className,
}: PeriodFilterProps) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

  // Roving tabindex: arrows move focus AND selection, which is the expected
  // behaviour for a radiogroup and saves a second keystroke to commit.
  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = presets.length - 1;
    let next: number | null = null;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;

    e.preventDefault();
    const preset = presets[next];
    if (!preset) return;
    refs.current[next]?.focus();
    onChange({ preset, from, to });
  }

  const showSteppers = steppable && !!onStep && CLOSED_PRESETS.has(value);

  const steppers = showSteppers ? (
    <div className="flex items-center gap-0.5">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-h-11 px-2"
        aria-label={`Previous ${PERIOD_LABELS[value].toLowerCase()}`}
        onClick={() => onStep?.(-1)}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-h-11 px-2"
        aria-label={`Next ${PERIOD_LABELS[value].toLowerCase()}`}
        onClick={() => onStep?.(1)}
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  ) : null;

  // MENU VARIANT — a native single-choice control.
  //
  // D-4 (2026-08-09) required radiogroup semantics over `aria-pressed` because
  // the presets are mutually exclusive. `Select` IS single-choice, so the
  // requirement is met by construction here; bolting ARIA onto it would be
  // describing the widget twice.
  if (variant === 'menu') {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={value}
            onValueChange={(next) => onChange({ preset: next as PeriodPreset, from, to })}
          >
            <SelectTrigger className="h-10 w-auto min-w-[11rem]" aria-label={label}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {presets.map((preset) => (
                <SelectItem key={preset} value={preset}>
                  {PERIOD_LABELS[preset]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {steppers}
        </div>

        {value === 'custom' && (
          <DateRangeField
            from={from}
            to={to}
            onChange={(next) => onChange({ preset: 'custom', ...next })}
          />
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-2">
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
        {presets.map((preset, i) => {
          const selected = preset === value;
          return (
            <Button
              key={preset}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              size="sm"
              variant={selected ? 'primary' : 'outline'}
              // 44px minimum touch target; `size="sm"` alone is shorter.
              className="min-h-11"
              onKeyDown={(e) => onKeyDown(e, i)}
              onClick={() => onChange({ preset, from, to })}
            >
              {PERIOD_LABELS[preset]}
            </Button>
          );
        })}
      </div>
        {steppers}
      </div>

      {value === 'custom' && (
        <DateRangeField
          from={from}
          to={to}
          onChange={(next) => onChange({ preset: 'custom', ...next })}
        />
      )}
    </div>
  );
}

export { PeriodFilter };
